// API Configuration and Connection Helper
// Centralized configuration for all API calls to Supabase Edge Functions

const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

/**
 * Supabase Edge Functions must be called as .../functions/v1/<slug>.
 * If REACT_APP_API_URL ends at .../functions/v1 (missing slug), append the slug or requests become /functions/v1/api/... → 404.
 */
function normalizeBackendBaseUrl(raw) {
  let url = String(raw || '').trim().replace(/\/+$/, '');
  if (!url) return 'http://localhost:3001';
  const slug =
    process.env.REACT_APP_SUPABASE_EDGE_FUNCTION ||
    process.env.REACT_APP_SUPABASE_BACKEND_FUNCTION ||
    'tourasco-backend';
  if (/\/functions\/v1$/i.test(url)) {
    return `${url}/${slug}`;
  }
  return url;
}

/**
 * Get the backend API URL from environment variables
 * Falls back to localhost for development if not set
 */
export const getBackendApiUrl = () => {
  const apiUrl = normalizeBackendBaseUrl(process.env.REACT_APP_API_URL || 'http://localhost:3001');

  // Log connection info in development
  if (process.env.NODE_ENV === 'development') {
    const isSupabase = apiUrl.includes('supabase.co');
    console.log('🔗 API Configuration:', {
      url: apiUrl,
      isSupabase,
      isLocalhost: apiUrl.includes('localhost'),
      environment: process.env.NODE_ENV,
      hasAuthKey: !!SUPABASE_ANON_KEY,
    });
  }

  return apiUrl;
};

/**
 * Get headers with Supabase authentication if needed
 */
export const getApiHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
  };
  
  const apiUrl = getBackendApiUrl();
  
  // Add Supabase auth header if using Supabase Edge Functions
  // Supabase Edge Functions accept Authorization Bearer token
  if (apiUrl.includes('supabase.co') && SUPABASE_ANON_KEY) {
    headers['Authorization'] = `Bearer ${SUPABASE_ANON_KEY}`;
  }
  
  return headers;
};

/**
 * Make a fetch request to the Supabase Edge Function backend
 * Includes proper error handling and CORS support
 */
export const fetchFromBackend = async (endpoint, options = {}) => {
  const baseUrl = getBackendApiUrl();
  const url = `${baseUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
  
  const defaultHeaders = getApiHeaders();
  
  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    
    // Handle non-JSON responses (like images)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error?.description || data.error?.code || `HTTP ${response.status}`);
      }
      
      return data;
    } else {
      // Return response as-is for non-JSON (images, etc.)
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return response;
    }
  } catch (error) {
    console.error(`❌ API Error (${endpoint}):`, error);
    throw error;
  }
};

/**
 * Test the connection to Supabase Edge Functions
 * Returns true if backend is reachable
 */
export const testBackendConnection = async () => {
  try {
    const baseUrl = getBackendApiUrl();
    const response = await fetch(`${baseUrl}/api/health`, {
      method: 'GET',
      headers: getApiHeaders(),
    });
    
    if (response.ok) {
      const data = await response.json();
      console.log('✅ Backend connection successful:', data);
      return true;
    } else {
      console.warn('⚠️ Backend connection failed:', response.status);
      return false;
    }
  } catch (error) {
    console.error('❌ Backend connection error:', error);
    return false;
  }
};

export default {
  getBackendApiUrl,
  getApiHeaders,
  fetchFromBackend,
  testBackendConnection,
};
